import {
  EventOrigin,
  MuteAction,
} from "@faire/deprecated-retailer-visitor-shared/features/InsightsFeed/events";
import { Core, Decorative } from "@faire/design-tokens";
import { Announcement } from "@faire/slate/icons/Announcement";
import { route as getInsightsPreferencesRoute } from "@faire/web-api--source/endpoints/www/api/insights/preferences/brandToken/get";
import postMuteStatus from "@faire/web-api--source/endpoints/www/api/insights/preferences/brandToken/post";
import { trackMyBrandsMuteInsightsButtonClick } from "@faire/web-api--source/events/myBrands/click/muteInsightsButton";
import { trackMyBrandsMuteInsightsModalView } from "@faire/web-api--source/events/myBrands/view/muteInsightsModal";
import { IRetailerBrandInsightPreference } from "@faire/web-api--source/indigofair/retailer/IRetailerBrandInsightPreference";
import { IRetailerBrandInsightPreferenceRequest } from "@faire/web-api--source/indigofair/retailer/IRetailerBrandInsightPreferenceRequest";
import { Button } from "@faire/web/slate/Button";
import { Modal } from "@faire/web/slate/deprecated/Modal";
import { AnnouncementMuted } from "@faire/web/slate/icons/foundation/Announcement";
import { Column } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { createToast } from "@faire/web/slate/Toast/Toast";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import styled from "styled-components";

interface IMuteModalProps {
  brandName: string;
  brandToken: string;
  isMuted: boolean;
  onClose: () => void;
  containerOrigin: EventOrigin | string;
  onChangeMute?: (brandToken: string) => void;
}

export const MuteModal: React.FC<IMuteModalProps> = ({
  brandName,
  brandToken,
  isMuted,
  onClose,
  containerOrigin,
  onChangeMute,
}) => {
  const queryClient = useQueryClient();
  const muteStatusMutation = useMutation({
    mutationFn: (requestParams: IRetailerBrandInsightPreferenceRequest) =>
      postMuteStatus(brandToken, requestParams),
    onSuccess: (data) => {
      queryClient.setQueryData([getInsightsPreferencesRoute, brandToken], data);
      onChangeMute?.(brandToken);
      trackMyBrandsMuteInsightsButtonClick(
        containerOrigin,
        isMuted ? MuteAction.Unmute : MuteAction.Mute,
        brandToken
      );
      createToast(
        isMuted ? (
          <StrictLocalMsg
            id="rEAuUv"
            defaultMessage="Updates unmuted"
            description={{
              text: "Toast message shown to user after successfully change the user preference",
              img: "https://cdn.faire.com/fastly/27e58ef4b23c3e9b21fff2ef9fb31da66defd7f63926c67754009c6091e6646d.png",
            }}
          />
        ) : (
          <StrictLocalMsg
            id="NwBKrQ"
            defaultMessage="Updates muted"
            description={{
              text: "Toast message shown to user after successfully change the user preference",
              img: "https://cdn.faire.com/fastly/27e58ef4b23c3e9b21fff2ef9fb31da66defd7f63926c67754009c6091e6646d.png",
            }}
          />
        ),
        {
          icon: isMuted
            ? "foundation:Announcement"
            : "foundation:AnnouncementMuted",
          timeout: "short",
        }
      );
      onClose();
    },
  });

  React.useEffect(() => {
    trackMyBrandsMuteInsightsModalView(containerOrigin, brandToken);
  }, [containerOrigin, brandToken]);

  const handleMute = async () => {
    const insightPreference = IRetailerBrandInsightPreference.build({
      brand_token: brandToken,
      is_muted: !isMuted,
    });

    const insightPreferenceRequestData =
      IRetailerBrandInsightPreferenceRequest.build({
        retailer_brand_insight_preference: insightPreference,
      });

    muteStatusMutation.reset();
    muteStatusMutation.mutate(insightPreferenceRequestData);
  };

  const handleDismiss = () => {
    trackMyBrandsMuteInsightsButtonClick(
      containerOrigin,
      MuteAction.Dismiss,
      brandToken
    );
    onClose();
  };

  return (
    <Modal height="auto" padding="24px" onClose={handleDismiss}>
      <Column>
        <Spacer height="8x" />
        <IconWrapper>
          {isMuted ? (
            <Announcement fontSize="24px" />
          ) : (
            <AnnouncementMuted fontSize="24px" />
          )}
        </IconWrapper>
        <Spacer height="4x" />
        <Typography variant="displaySSerifRegular">
          {isMuted ? (
            <StrictLocalMsg
              id="7VUnJj"
              defaultMessage="Unmute updates from {brandProductName}?"
              values={{ brandProductName: brandName }}
              description={{
                text: "Indicate the action to unmate a brand",
                img: "https://cdn.faire.com/fastly/16b1678aa5b84679d32ab85c63fd801ca286e83822fd46f6e6ce250138aced62.png",
              }}
            />
          ) : (
            <StrictLocalMsg
              id="ITocjf"
              defaultMessage="Mute updates from {brandProductName}?"
              values={{ brandProductName: brandName }}
              description={{
                text: "Indicate the action to mate a brand",
                img: "https://cdn.faire.com/fastly/10dd800190593f9c141eba825940df5121c63e9f4c6800fd7cf4b616b179cfbd.png",
              }}
            />
          )}
        </Typography>
        <Spacer height="4x" />
        {isMuted ? (
          <Typography>
            <StrictLocalMsg
              id="QvspLl"
              defaultMessage="Get updates when this brand has new products, promotions, and more."
              description={{
                text: "Message regarding the result of the unmute",
                img: "https://cdn.faire.com/fastly/16b1678aa5b84679d32ab85c63fd801ca286e83822fd46f6e6ce250138aced62.png",
              }}
            />
          </Typography>
        ) : (
          <>
            <Typography>
              <StrictLocalMsg
                id="R21Tlr"
                defaultMessage="You’ll no longer get updates when this brand has new products, promotions, and more."
                description={{
                  text: "Message regarding the result of the mute",
                  img: "https://cdn.faire.com/fastly/10dd800190593f9c141eba825940df5121c63e9f4c6800fd7cf4b616b179cfbd.png",
                }}
              />
            </Typography>
            <Spacer height="2x" />
            <Typography>
              <StrictLocalMsg
                id="FaYLC1"
                defaultMessage="To get these updates again, unmute updates on their brand page."
                description={{
                  text: "Message indicating how to get updates again",
                  img: "https://cdn.faire.com/fastly/10dd800190593f9c141eba825940df5121c63e9f4c6800fd7cf4b616b179cfbd.png",
                }}
              />
            </Typography>
          </>
        )}
        <Spacer height="4x" />
        <Button
          variant="primary"
          onClick={handleMute}
          loading={muteStatusMutation.isPending}
          data-test-id="MuteModalButton"
        >
          {isMuted ? (
            <StrictLocalMsg
              id="yFOhnN"
              defaultMessage="Unmute updates"
              description={{
                text: "Button to unmute updates",
                img: "https://cdn.faire.com/fastly/10dd800190593f9c141eba825940df5121c63e9f4c6800fd7cf4b616b179cfbd.png",
              }}
            />
          ) : (
            <StrictLocalMsg
              id="WdKrem"
              defaultMessage="Mute updates"
              description={{
                text: "Button to mute updates",
                img: "https://cdn.faire.com/fastly/10dd800190593f9c141eba825940df5121c63e9f4c6800fd7cf4b616b179cfbd.png",
              }}
            />
          )}
        </Button>
        {muteStatusMutation.isError ? (
          <>
            <Spacer height="1x" />
            <Typography color={Decorative.shade.orange} align="center">
              <StrictLocalMsg
                id="a5o2rT"
                defaultMessage="Something went wrong, please try again"
                description={{
                  text: "Message indicating an error when submitting the change of update preference",
                  img: "https://cdn.faire.com/fastly/0fd11fa6c6455a30079654c85708028f9ea4b5520d19dfafc79f31b2cb418c53.png",
                }}
              />
            </Typography>
          </>
        ) : null}
        <Spacer height="2x" />
        <Button variant="tertiary" onClick={handleDismiss}>
          <StrictLocalMsg id="47FYwb" defaultMessage="Cancel" />
        </Button>
        <Spacer height="2x" />
      </Column>
    </Modal>
  );
};

const IconWrapper = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background-color: ${Core.surface.subdued};
  display: flex;
  align-items: center;
  justify-content: center;
`;
